<script>
  import { GQLNow } from "gql-now";
  import { onMount } from "svelte";
  import { CSVDownloader } from "svelte-csv";
  import moment from "moment";
  import {
    f7,
    f7ready,
    Page,
    Navbar,
    NavLeft,
    NavTitle,
    NavRight,
    Link,
    Icon,
    Badge,
    BlockTitle,
    Block,
    Card,
    CardContent,
    CardHeader,
    Row,
    Col,
    Button,
    List,
    ListItem,
    AccordionContent,
    Panel,
  } from "framework7-svelte";
  import { handleUnauthorized } from "../js/auth";

  export let f7route;
  export let f7router;

  let admin_access_token = "";
  let loading = true;
  let summary = {
    total_referrals: 0,
    approved_referrals: 0,
    pending_referrals: 0,
    incomplete_referrals: 0,
    funded_referrals: 0,
    total_funded_amount: 0,
    unique_referrers: 0,
    conversion_rate: 0,
  };
  let top_referrers = [];
  let all_referrals = [];
  let filtered_referrals = [];

  let search_query = "";
  let active_filter = "all"; // 'all', 'approved', 'pending', 'incomplete', 'funded'
  let export_filename = "trdr_referrals_" + moment().format("DD-MM-YYYY");

  onMount(() => {
    f7ready(async () => {
      admin_access_token = localStorage.getItem("token");
      if (!admin_access_token) {
        handleUnauthorized(f7, f7router);
        return;
      }
      await fetch_referral_data();
    });
  });

  const fetch_referral_data = async () => {
    loading = true;
    let obj = new GQLNow(`https://api.trdr.money/graphql/trdr-admin-services`, {
      "x-trdr-org": "60e6cd9df9c32f00072b71f8",
      "x-access-token": admin_access_token,
    });
    try {
      f7.preloader.show();
      let response = await obj.query(
        "fetch_all_referrals",
        {},
        "status summary { total_referrals approved_referrals pending_referrals incomplete_referrals funded_referrals total_funded_amount unique_referrers conversion_rate } top_referrers { referral_code referrer_name referrer_mobile referrer_ucc total_referred approved_count funded_count total_volume } referrals { referee_user_id referee_name referee_mobile referee_email referee_ucc referee_status referral_code referrer_name referrer_mobile referrer_ucc created_ts created_date amount_added portfolio_value ancestors }"
      );
      f7.preloader.hide();
      loading = false;

      if (response && response.data && response.data.fetch_all_referrals) {
        let data = response.data.fetch_all_referrals;
        if (data.summary) summary = data.summary;
        if (data.top_referrers) top_referrers = data.top_referrers;
        if (data.referrals) {
          all_referrals = data.referrals;
          apply_filter();
        }
      }
    } catch (err) {
      f7.preloader.hide();
      loading = false;
      console.log("Fetch referrals error:", err);
      let errMsg = "";
      if (err && err.data && err.data.errors && err.data.errors.length > 0) {
        errMsg = err.data.errors[0].message || "";
      } else if (err && err.message) {
        errMsg = err.message;
      } else if (typeof err === "string") {
        errMsg = err;
      }
      if (
        errMsg.includes("Access Denied") ||
        errMsg.includes("Unauthorized") ||
        errMsg.includes("authenticate token") ||
        errMsg.includes("Member not found")
      ) {
        handleUnauthorized(f7, f7router, "Invalid credentials or session expired");
      } else {
        f7.dialog.alert(errMsg || "Failed to load referrals data");
      }
    }
  };

  function apply_filter() {
    let result = all_referrals;

    if (active_filter === "approved") {
      result = result.filter((r) => r.referee_status === "approved");
    } else if (active_filter === "pending") {
      result = result.filter((r) => r.referee_status === "pending");
    } else if (active_filter === "incomplete") {
      result = result.filter((r) => r.referee_status === "incomplete" || !r.referee_status);
    } else if (active_filter === "funded") {
      result = result.filter((r) => r.amount_added > 0 || r.portfolio_value > 0);
    }

    if (search_query && search_query.trim() !== "") {
      let q = search_query.toLowerCase().trim();
      result = result.filter((r) => {
        return (
          (r.referee_name && r.referee_name.toLowerCase().includes(q)) ||
          (r.referee_mobile && r.referee_mobile.toLowerCase().includes(q)) ||
          (r.referee_ucc && r.referee_ucc.toLowerCase().includes(q)) ||
          (r.referee_email && r.referee_email.toLowerCase().includes(q)) ||
          (r.referral_code && r.referral_code.toLowerCase().includes(q)) ||
          (r.referrer_name && r.referrer_name.toLowerCase().includes(q)) ||
          (r.referrer_mobile && r.referrer_mobile.toLowerCase().includes(q)) ||
          (r.referrer_ucc && r.referrer_ucc.toLowerCase().includes(q))
        );
      });
    }

    filtered_referrals = result;
  }

  function set_filter(filter_name) {
    active_filter = filter_name;
    apply_filter();
  }

  function on_search(e) {
    search_query = e.target.value;
    apply_filter();
  }

  function format_currency(val) {
    let num = Number(val) || 0;
    return "₹" + num.toLocaleString("en-IN");
  }

  function filter_by_referrer(code) {
    search_query = code;
    apply_filter();
  }
</script>

<Page class="referrals-page">
  <Navbar>
    <NavLeft>
      <Link
        iconIos="f7:menu"
        iconAurora="f7:menu"
        iconMd="material:menu"
        panelOpen="#panel-left"
      />
    </NavLeft>
    <NavTitle>Referrals & Acquisition</NavTitle>
    <NavRight>
      <Link onClick={fetch_referral_data}>
        <Icon f7="arrow_clockwise" color="blue" />
      </Link>
    </NavRight>
  </Navbar>

  <!-- Left Sidebar Panel -->
  <Panel left cover id="panel-left">
    <Page>
      <BlockTitle>Admin</BlockTitle>
      <List>
        <ListItem panelClose link="/admin-home/" title="Home">
          <i slot="media"> <Icon f7="house" color="black" /></i>
        </ListItem>
        <ListItem panelClose link="/referrals/" title="Referrals">
          <i slot="media"> <Icon f7="person_2_fill" color="black" /></i>
        </ListItem>
        <ListItem accordionItem title="Users">
          <i slot="media"> <Icon f7="person_crop_rectangle" color="black" /></i>
          <AccordionContent>
            <List>
              <ListItem panelClose link="/users/" title="All Users" />
              <ListItem panelClose link="/approved-users/" title="Approved Users" />
              <ListItem panelClose link="/incomplete-users/" title="Incomplete Users" />
              <ListItem panelClose link="/pending-users/" title="Pending Users" />
            </List>
          </AccordionContent>
        </ListItem>
        <ListItem accordionItem title="ENACH">
          <i slot="media"> <Icon f7="arrow_clockwise_circle" color="black" /></i>
          <AccordionContent>
            <List>
              <ListItem panelClose link="/all-enach/" title="All Enach" />
              <ListItem panelClose link="/enach-mandate-reg-succ/" title="Register Success" />
              <ListItem panelClose link="/enach-mandate-auth-succ/" title="Auth Success" />
              <ListItem panelClose link="/enach-mandate-partial/" title="Partial" />
            </List>
          </AccordionContent>
        </ListItem>
        <ListItem accordionItem title="DEBITS">
          <i slot="media"> <Icon f7="money_dollar_circle" color="black" /></i>
          <AccordionContent>
            <List>
              <ListItem panelClose link="/enach-debit-all/" title="All Debits" />
              <ListItem panelClose link="/enach-debit-success/" title="Success Debits" />
              <ListItem panelClose link="/enach-debit-fail/" title="Failed Debits" />
            </List>
          </AccordionContent>
        </ListItem>
        <ListItem panelClose link="/all-users-home-data/" title="Home Data">
          <i slot="media"> <Icon f7="ellipses_bubble" color="black" /></i>
        </ListItem>
        <ListItem panelClose link="/cashfree-transactions/" title="Cashfree Transactions">
          <i slot="media"> <Icon f7="money_dollar_circle" color="black" /></i>
        </ListItem>
        <ListItem panelClose link="/requested-callbacks/" title="Requested Callbacks">
          <i slot="media"> <Icon f7="phone" color="black" /></i>
        </ListItem>
        <ListItem panelClose link="/approve-account/" title="Approve Account">
          <i slot="media"> <Icon f7="checkmark_alt_circle" color="black" /></i>
        </ListItem>
        <ListItem panelClose link="/withdraw-requests/" title="Withdraw Requests">
          <i slot="media"> <Icon f7="money_dollar_circle" color="black" /></i>
        </ListItem>
        <ListItem panelClose link="/verify-penny-drop/" title="Penny Drop">
          <i slot="media"> <Icon f7="checkmark_alt_circle" color="black" /></i>
        </ListItem>
      </List>
    </Page>
  </Panel>

  <!-- Main Content Container -->
  <div class="content-container">
    <!-- Executive KPI Summary Cards for Boss -->
    <div class="kpi-grid">
      <div class="kpi-card purple">
        <div class="kpi-title">Total Referrals</div>
        <div class="kpi-val">{summary.total_referrals}</div>
        <div class="kpi-sub">Across {summary.unique_referrers} Unique Referrers</div>
      </div>

      <div class="kpi-card green">
        <div class="kpi-title">Approved & Converted</div>
        <div class="kpi-val">{summary.approved_referrals}</div>
        <div class="kpi-sub">{summary.conversion_rate}% Conversion Rate</div>
      </div>

      <div class="kpi-card blue">
        <div class="kpi-title">Funded Referrals</div>
        <div class="kpi-val">{summary.funded_referrals}</div>
        <div class="kpi-sub">{format_currency(summary.total_funded_amount)} Total Volume / AUM</div>
      </div>

      <div class="kpi-card orange">
        <div class="kpi-title">Pending / Incomplete</div>
        <div class="kpi-val">{summary.pending_referrals + summary.incomplete_referrals}</div>
        <div class="kpi-sub">{summary.pending_referrals} pending approval, {summary.incomplete_referrals} incomplete</div>
      </div>
    </div>

    <!-- Top Referrers Leaderboard Section -->
    {#if top_referrers && top_referrers.length > 0}
      <Card class="section-card">
        <CardHeader class="section-header">
          <div class="header-with-icon">
            <Icon f7="star_fill" color="yellow" size="20" />
            <span class="header-text">Top Referrers & Promoter Champions</span>
          </div>
          <Badge color="blue">{top_referrers.length} Active Promoters</Badge>
        </CardHeader>
        <CardContent>
          <div class="leaderboard-grid">
            {#each top_referrers.slice(0, 6) as promoter, idx}
              <div class="promoter-box" on:click={() => filter_by_referrer(promoter.referral_code)}>
                <div class="promoter-badge">#{idx + 1}</div>
                <div class="promoter-info">
                  <div class="promoter-name">{promoter.referrer_name || promoter.referral_code}</div>
                  <div class="promoter-meta">
                    Code: <span class="badge-code">{promoter.referral_code}</span>
                    {#if promoter.referrer_ucc}
                      | UCC: {promoter.referrer_ucc}
                    {/if}
                  </div>
                  <div class="promoter-stats">
                    <span class="stat-item"><b>{promoter.total_referred}</b> Refers</span>
                    <span class="stat-item text-green"><b>{promoter.approved_count}</b> Approved</span>
                    {#if promoter.total_volume > 0}
                      <span class="stat-item text-blue"><b>{format_currency(promoter.total_volume)}</b> Volume</span>
                    {/if}
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </CardContent>
      </Card>
    {/if}

    <!-- Referrals List Table with Search & Filters -->
    <Card class="section-card">
      <CardHeader class="table-card-header">
        <div class="header-left">
          <span class="header-title">Who Referred Whom ({filtered_referrals.length})</span>
        </div>
        <div class="header-right">
          {#if filtered_referrals && filtered_referrals.length > 0}
            <div class="csv-btn-wrapper">
              <CSVDownloader data={filtered_referrals} filename={export_filename} bom={true}>
                <Button fill small color="green" class="export-btn">
                  <Icon f7="arrow_down_doc_fill" size="14" /> Download CSV
                </Button>
              </CSVDownloader>
            </div>
          {/if}
        </div>
      </CardHeader>

      <CardContent>
        <!-- Filter Bar & Search Input -->
        <div class="filter-toolbar">
          <div class="search-box">
            <input
              type="text"
              placeholder="Search referee name, mobile, UCC or referrer code..."
              value={search_query}
              on:input={on_search}
              class="custom-search-input"
            />
            {#if search_query}
              <button class="clear-search" on:click={() => { search_query = ""; apply_filter(); }}>✕</button>
            {/if}
          </div>

          <div class="filter-tabs">
            <button
              class="tab-btn {active_filter === 'all' ? 'active' : ''}"
              on:click={() => set_filter('all')}
            >
              All ({summary.total_referrals})
            </button>
            <button
              class="tab-btn {active_filter === 'approved' ? 'active' : ''}"
              on:click={() => set_filter('approved')}
            >
              Approved ({summary.approved_referrals})
            </button>
            <button
              class="tab-btn {active_filter === 'pending' ? 'active' : ''}"
              on:click={() => set_filter('pending')}
            >
              Pending ({summary.pending_referrals})
            </button>
            <button
              class="tab-btn {active_filter === 'incomplete' ? 'active' : ''}"
              on:click={() => set_filter('incomplete')}
            >
              Incomplete ({summary.incomplete_referrals})
            </button>
            <button
              class="tab-btn {active_filter === 'funded' ? 'active' : ''}"
              on:click={() => set_filter('funded')}
            >
              Funded ({summary.funded_referrals})
            </button>
          </div>
        </div>

        <!-- Table View -->
        <div class="table-responsive">
          <table class="referrals-table">
            <thead>
              <tr>
                <th>Referee (Referred User)</th>
                <th>Mobile / Email</th>
                <th>Referred By (Referrer)</th>
                <th>Referral Code</th>
                <th>Status</th>
                <th>Signup Date</th>
                <th>Funded / Portfolio</th>
              </tr>
            </thead>
            <tbody>
              {#if filtered_referrals.length === 0}
                <tr>
                  <td colspan="7" class="empty-state">
                    {#if loading}
                      Loading referrals data...
                    {:else}
                      No referral records match the selected criteria.
                    {/if}
                  </td>
                </tr>
              {:else}
                {#each filtered_referrals as item}
                  <tr>
                    <td>
                      <div class="user-cell">
                        <span class="user-name">{item.referee_name || "—"}</span>
                        {#if item.referee_ucc}
                          <span class="ucc-badge">UCC: {item.referee_ucc}</span>
                        {/if}
                      </div>
                    </td>
                    <td>
                      <div class="contact-cell">
                        <div class="mobile-text">{item.referee_mobile || "—"}</div>
                        <div class="email-text">{item.referee_email || ""}</div>
                      </div>
                    </td>
                    <td>
                      <div class="referrer-cell">
                        <span class="referrer-name">{item.referrer_name || item.referral_code}</span>
                        {#if item.referrer_mobile}
                          <span class="referrer-sub">{item.referrer_mobile}</span>
                        {/if}
                      </div>
                    </td>
                    <td>
                      <span class="ref-code-tag" on:click={() => filter_by_referrer(item.referral_code)}>
                        {item.referral_code}
                      </span>
                    </td>
                    <td>
                      {#if item.referee_status === "approved"}
                        <Badge color="green">Approved</Badge>
                      {:else if item.referee_status === "pending"}
                        <Badge color="orange">Pending</Badge>
                      {:else}
                        <Badge color="gray">Incomplete</Badge>
                      {/if}
                    </td>
                    <td>
                      <span class="date-text">{item.created_date || item.created_ts || "—"}</span>
                    </td>
                    <td>
                      {#if item.amount_added > 0 || item.portfolio_value > 0}
                        <span class="funded-amount text-green">
                          {format_currency(item.amount_added || item.portfolio_value)}
                        </span>
                      {:else}
                        <span class="not-funded">—</span>
                      {/if}
                    </td>
                  </tr>
                {/each}
              {/if}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  </div>
</Page>

<style>
  .content-container {
    padding: 16px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }

  .kpi-card {
    padding: 20px;
    border-radius: 12px;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .kpi-card.purple {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .kpi-card.green {
    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  }

  .kpi-card.blue {
    background: linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%);
  }

  .kpi-card.orange {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  }

  .kpi-title {
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.9;
  }

  .kpi-val {
    font-size: 32px;
    font-weight: 700;
    margin: 8px 0 4px 0;
  }

  .kpi-sub {
    font-size: 13px;
    opacity: 0.85;
  }

  .section-card {
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f0f0f0;
    padding: 12px 16px;
  }

  .header-with-icon {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .header-text {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
  }

  .leaderboard-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 12px;
  }

  .promoter-box {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .promoter-box:hover {
    border-color: #667eea;
    background: #f1f5f9;
    transform: translateY(-2px);
  }

  .promoter-badge {
    background: #4f46e5;
    color: white;
    font-weight: bold;
    font-size: 14px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .promoter-info {
    flex-grow: 1;
  }

  .promoter-name {
    font-weight: 600;
    font-size: 15px;
    color: #1e293b;
  }

  .promoter-meta {
    font-size: 12px;
    color: #64748b;
    margin: 2px 0 6px 0;
  }

  .badge-code {
    background: #e0e7ff;
    color: #3730a3;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
  }

  .promoter-stats {
    display: flex;
    gap: 12px;
    font-size: 12px;
  }

  .stat-item {
    color: #475569;
  }

  .text-green {
    color: #16a34a !important;
  }

  .text-blue {
    color: #0284c7 !important;
  }

  .table-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .header-title {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
  }

  .filter-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .search-box {
    position: relative;
    flex: 1;
    min-width: 280px;
    max-width: 480px;
  }

  .custom-search-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
  }

  .custom-search-input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }

  .clear-search {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 14px;
  }

  .filter-tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .tab-btn {
    padding: 8px 14px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .tab-btn.active {
    background: #4f46e5;
    color: #ffffff;
    border-color: #4f46e5;
  }

  .table-responsive {
    overflow-x: auto;
  }

  .referrals-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .referrals-table th {
    background: #f1f5f9;
    color: #334155;
    font-weight: 600;
    text-align: left;
    padding: 12px 14px;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
  }

  .referrals-table td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: middle;
  }

  .referrals-table tr:hover {
    background: #f8fafc;
  }

  .user-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .user-name {
    font-weight: 600;
    color: #0f172a;
  }

  .ucc-badge {
    font-size: 11px;
    color: #64748b;
  }

  .contact-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mobile-text {
    font-weight: 500;
  }

  .email-text {
    font-size: 11px;
    color: #64748b;
  }

  .referrer-cell {
    display: flex;
    flex-direction: column;
  }

  .referrer-name {
    font-weight: 600;
    color: #2563eb;
  }

  .referrer-sub {
    font-size: 11px;
    color: #64748b;
  }

  .ref-code-tag {
    background: #eff6ff;
    color: #1d4ed8;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
    font-family: monospace;
  }

  .ref-code-tag:hover {
    background: #dbeafe;
    text-decoration: underline;
  }

  .date-text {
    color: #64748b;
    white-space: nowrap;
  }

  .funded-amount {
    font-weight: 600;
  }

  .not-funded {
    color: #94a3b8;
  }

  .empty-state {
    text-align: center;
    padding: 30px;
    color: #64748b;
    font-style: italic;
  }

  .export-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
</style>
